import i18next from 'i18next';
import { z } from 'zod';

const integerErrorMessage = i18next.t('general.error.integer');
const positiveNumber = i18next.t('general.error.positive');
const invalidFormat = i18next.t('general.error.invalidFormat');
const invalidNumberFormat = i18next.t('general.error.invalidNumber');
const invalidDateMessage = i18next.t('general.error.invalidDate');
export const invalidActiveToDateMessage = i18next.t('general.error.invalidDateActiveTo');
const requiredFieldMessage = i18next.t('general.error.requiredField');
const noiseThresholdMessage = i18next.t('general.error.noiseThreshold');
const latitudeErrorMessage = i18next.t('management.substation.latitude.error');
const longitudeErrorMessage = i18next.t('management.substation.longitude.error');
const latitudeMessage = i18next.t('general.error.latitude');
const longitudeMessage = i18next.t('general.error.longitude');
const transducerMessage = i18next.t('masterdata.transducer.table.transmissionRatioFormat');

export const pastDateTime = z.string().refine((date) => new Date(date) <= new Date(), {
  error: invalidDateMessage,
});

export const pastDateActiveToTime = z.string().refine((date) => new Date(date) <= new Date(), {
  error: invalidActiveToDateMessage,
});

export const pastYear = z.number().lte(new Date().getFullYear(), {
  error: invalidDateMessage,
});

export const mandatory = z
  .union([z.string(), z.number(), z.looseObject({}), z.array(z.any())])
  .refine((value) => value !== null && value !== undefined, { error: requiredFieldMessage });

export const positiveIntegerSchema = z.number().int({ error: integerErrorMessage }).positive({ error: positiveNumber });

export const stringPositiveIntegerSchema = z
  .union([z.number(), z.string(), z.null(), z.undefined()])
  .transform((value) => {
    if (value === null || value === undefined) return undefined;
    return typeof value === 'string' ? Number(value) : value;
  })
  .refine((value) => value === undefined || Number.isInteger(value), { error: integerErrorMessage })
  .refine((value) => value === undefined || value > 0, { error: positiveNumber });

export const mandatoryStringPositiveIntegerSchema = z
  .union([z.number(), z.string().min(1, requiredFieldMessage)])
  .transform((value) => {
    return typeof value === 'string' ? Number(value) : value;
  })
  .refine((value) => Number.isInteger(value), { error: integerErrorMessage })
  .refine((value) => value > 0, { error: positiveNumber });

export const baseNumberSchema = z
  .union([z.coerce.number(), z.literal(''), z.null()])
  .transform((value) => {
    if (value === '' || value === null) return undefined;
    return value;
  })
  .refine((value) => value === undefined || typeof value === 'number', { error: invalidFormat });

export const stringSchema = z
  .union([z.string(), z.literal(''), z.null()])
  .transform((value) => {
    if (value === '' || value === null) return undefined;
    return value;
  })
  .refine((value) => value === undefined || /^[a-zA-Z0-9äöüÄÖÜß\s\-_()/]{1,255}$/.test(value), {
    error: invalidFormat,
  });

export const mandatoryStringSchema = z
  .string()
  .trim()
  .min(1, requiredFieldMessage)
  .refine((value) => /^[a-zA-Z0-9äöüÄÖÜß\s\-_()/]{1,255}$/.test(value), {
    error: invalidFormat,
  });

export const mandatoryBaseNumberSchema = z
  .string()
  .trim()
  .min(1, requiredFieldMessage)
  .refine((value) => !Number.isNaN(Number.parseFloat(value)), { error: invalidNumberFormat });

export const dateString = z.union([z.string(), z.null(), z.undefined()]).refine((value) => {
  if (value === undefined || value === null) {
    return true;
  }
  const parsed = Date.parse(value);
  return !Number.isNaN(parsed);
});

export const baseOption = z.union([z.number(), z.null(), z.undefined()]).refine((value) => {
  if (value === undefined || value === null) {
    return true;
  }
  return value >= 0;
});

export const commentSchema = z.union([z.string(), z.literal('')]).optional();

export const measurementDeviceNameSchema = z.string().regex(/^[a-zA-ZäöüÄÖÜß0-9]{1,255}$/, {
  error: i18next.t('management.measurementDevice.error.invalidFormat'),
});

export const noiseThresholdSchema = z
  .union([z.string(), z.number(), z.null(), z.undefined()])
  .transform((value) => {
    if (value === null || value === undefined || value === '') return undefined;
    if (typeof value === 'number') return `${value}`;
    return value;
  })
  .refine(
    (value) => {
      if (value === undefined) return true;

      const [integerPart, decimalPart] = value.split('.');

      const isPositive = Number.parseFloat(value) > 0;
      const validIntegerPart = integerPart?.length <= 3;
      const validDecimalPart = !decimalPart || decimalPart.length <= 5;

      return isPositive && validIntegerPart && validDecimalPart;
    },
    {
      error: noiseThresholdMessage,
    }
  );

export const latitudeSchema = z
  .union([z.string(), z.number()])
  .refine(
    (value) => {
      const [integerPart, decimalPart] = value.toString().replace(/^-/, '').split('.');

      const validIntegerPart = integerPart?.length <= 2;
      const validDecimalPart = decimalPart === undefined || decimalPart?.length <= 6;

      return validIntegerPart && validDecimalPart;
    },
    {
      error: latitudeMessage,
    }
  )
  .refine(
    (value) => {
      const latitude = Number(value);
      const validLatitude = Number.isFinite(latitude) && latitude >= -90 && latitude <= 90;

      return validLatitude;
    },
    {
      error: latitudeErrorMessage,
    }
  );

export const longitudeSchema = z
  .union([z.string(), z.number()])
  .refine(
    (value) => {
      const [integerPart, decimalPart] = value.toString().replace(/^-/, '').split('.');

      const validIntegerPart = integerPart?.length <= 3;
      const validDecimalPart = decimalPart === undefined || decimalPart?.length <= 6;

      return validIntegerPart && validDecimalPart;
    },
    {
      error: longitudeMessage,
    }
  )
  .refine(
    (value) => {
      const longitude = Number(value);
      const validLongitude = Number.isFinite(longitude) && longitude >= -180 && longitude <= 180;

      return validLongitude;
    },
    {
      error: longitudeErrorMessage,
    }
  );

export const transformerRatioSchema = z
  .string()
  .trim()
  .nonempty(requiredFieldMessage)
  .superRefine((value, ctx) => {
    const parts = value.split(':');

    if (parts.length !== 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: transducerMessage,
      });
      return;
    }

    parts.forEach((part) => {
      const numericValue = Number(part);

      if (Number.isNaN(numericValue)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: integerErrorMessage,
        });
        return;
      }

      if (!Number.isInteger(numericValue)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: integerErrorMessage,
        });
        return;
      }

      if (numericValue <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: positiveNumber,
        });
      }

      if (part.length > 6) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: transducerMessage,
        });
      }
    });
  });

export const mandatoryOption = z.preprocess(
  (val) => (val === '' || val === null || val === undefined ? undefined : Number(val)),
  z.number().refine((val) => !Number.isNaN(val), {
    error: requiredFieldMessage,
  })
);

export const mandatoryIntervalOption = z.coerce.number().refine((val) => val > 0, {
  error: requiredFieldMessage,
});

export const mandatorySelection = z
  .union([z.number(), z.string().transform(Number), z.null(), z.undefined()])
  .refine((val) => typeof val === 'number' && !Number.isNaN(val), {
    error: requiredFieldMessage,
  });

export const baseBooleanSchema = z.boolean();
